/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import {
  RouteDefinition,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";

export function ProfileNavigation({
  current,
  routeOverview,
  routeMyAccountDetails,
  routeMyAccountMerchantIntegration,
  routeMyAccountPassword,
}: {
  current: "details" | "credentials" | "merchant-integration";
  routeOverview: RouteDefinition;
  routeMyAccountDetails: RouteDefinition;
  routeMyAccountMerchantIntegration: RouteDefinition;
  routeMyAccountPassword: RouteDefinition;
}): VNode {
  const { i18n } = useTranslationContext();
  const links = [
    {
      id: "details" as const,
      label: i18n.str`Profile`,
      href: routeMyAccountDetails.url({}),
    },
    {
      id: "credentials" as const,
      label: i18n.str`Security`,
      href: routeMyAccountPassword.url({}),
    },
    {
      id: "merchant-integration" as const,
      label: i18n.str`Merchant integration`,
      href: routeMyAccountMerchantIntegration.url({}),
    },
  ];

  return (
    <div class="mb-6">
      <a
        class="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-primaryDark hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        href={routeOverview.url({})}
      >
        <svg
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
          />
        </svg>
        <i18n.Translate>Back to overview</i18n.Translate>
      </a>
      <nav aria-label={i18n.str`My account settings`}>
        <ul class="flex overflow-x-auto border-b border-gray-200">
          {links.map((link) => {
            const selected = link.id === current;
            return (
              <li key={link.id} class="shrink-0">
                <a
                  href={link.href}
                  aria-current={selected ? "page" : undefined}
                  data-selected={selected}
                  class="inline-flex min-h-11 items-center border-b-2 border-transparent px-4 text-sm font-semibold text-secondary hover:border-primary/40 hover:text-primaryDark data-[selected=true]:border-primary data-[selected=true]:text-primaryDark"
                >
                  {link.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
